import { createTheme } from 'widget_main/globals/themes';
import { InitCallbackResponse } from 'core_main/sdk/init/types';
import { getKustomer } from 'widget_main/globals/helpers';
import { ChatIconPosition, ChatIconSize } from 'widget/widget_api/types';
import { GetConversationsCallbackResponse } from 'core_main/sdk/get_conversations/types';
import { ConversationsState, Page } from 'widget_main/store/types';
import {
  CONVERSATIONS_PATH,
  DEFAULT_SESSION_PAGE_SIZE,
  KB_ARTICLE_PATH,
  KB_DEFLECT_ARTICLE_PATH,
  KB_SEARCH_PATH,
  MESSAGE_THREAD_PATH,
  WIDGET_TYPE_CHAT,
  WIDGET_TYPE_CHAT_KB,
  WIDGET_TYPE_KB,
} from 'widget_main/globals/constants';
import { DEFAULT_CHAT_ICON_SIZE } from './constants';

export const setThemeVariables = (
  settings: InitCallbackResponse['settings'],
) => {
  const theme = createTheme(settings);

  Object.entries(theme).forEach((value) => {
    const variable = value[0];
    const style = value[1];
    document.documentElement.style.setProperty(variable, style);
  });
  return Promise.resolve();
};

export const getChatIconSize = (): ChatIconSize => {
  const startParameters = getKustomer()?.startParameters;
  return (
    startParameters?.chatIconSize || {
      width: DEFAULT_CHAT_ICON_SIZE,
      height: DEFAULT_CHAT_ICON_SIZE,
    }
  );
};

export const getChatIconPosition = (): ChatIconPosition => {
  const { startParameters } = getKustomer();
  return startParameters.chatIconPosition;
};

const MIN_SESSION_HISTORY_PAGE_SIZE = 1;
const MAX_SESSION_HISTORY_PAGE_SIZE = 100;

/**
 * Page size for chat session history API (`getConversations` / `getSessions`).
 * Defaults to {@link DEFAULT_SESSION_PAGE_SIZE}; optional `Kustomer.start({ sessionHistoryPageSize })` for integration tests (e.g. **1** to force many pages), clamped to **1–100**.
 */
export const getSessionHistoryPageSize = (): number => {
  const raw = getKustomer()?.startParameters?.sessionHistoryPageSize;
  if (typeof raw !== 'number' || Number.isNaN(raw)) {
    return DEFAULT_SESSION_PAGE_SIZE;
  }
  return Math.min(
    MAX_SESSION_HISTORY_PAGE_SIZE,
    Math.max(MIN_SESSION_HISTORY_PAGE_SIZE, Math.floor(raw)),
  );
};

export const mapConversationState = (
  conversations: GetConversationsCallbackResponse['conversations'],
  conversationDataState: ConversationsState['data'],
): ConversationsState['data'] => {
  return conversations
    .map((conversation) => {
      const currentConversation =
        conversationDataState[conversation.conversationId];

      const optimisticMessages = currentConversation?.optimisticMessages || [];
      const messages = currentConversation?.messages || [];
      const satisfaction = currentConversation?.satisfaction;
      const assistantId = currentConversation?.assistantId;
      const hasAllMessages = currentConversation?.hasAllMessages;
      const createdAt =
        conversation.createdAt || currentConversation?.createdAt;
      const custom = conversation.custom ?? currentConversation?.custom;

      return {
        ...conversation,
        messages,
        optimisticMessages,
        satisfaction,
        assistantId,
        hasAllMessages,
        createdAt,
        custom,
      };
    })
    .reduce((acc, val) => {
      acc[val.conversationId] = val;
      return acc;
    }, {} as ConversationsState['data']);
};

/**
 * This is only needed to support testing live chat preview and the _rare_ case of an org
 *  changing their widget type to one that does not support a previous last page opened
 * @param lastOpenedPage
 * @param widgetType
 */
export const lastOpenedPageIsSupportedByWidgetType = (
  lastOpenedPage: Page,
  widgetType: string,
): boolean => {
  switch (lastOpenedPage) {
    case KB_ARTICLE_PATH:
    case KB_SEARCH_PATH:
    case KB_DEFLECT_ARTICLE_PATH:
      return [WIDGET_TYPE_KB, WIDGET_TYPE_CHAT_KB].includes(widgetType);
    case CONVERSATIONS_PATH:
    case MESSAGE_THREAD_PATH:
      return [WIDGET_TYPE_CHAT, WIDGET_TYPE_CHAT_KB].includes(widgetType);
    default:
      return false;
  }
};
